/**
 * Profile Service
 *
 * Handles user profile CRUD operations against the unified `user_profiles` table.
 * Profile creation for new signups is handled by a DB trigger on auth.users INSERT
 * (see handle_new_user() in Supabase). The createProfile() function here is kept
 * as a fallback and for admin/manual use.
 */

import { supabase, isSupabaseConfigured } from "./client";
import type { UserProfile, CreateUserProfileData } from "./types";
import { NO_EMAIL_PREFERENCE } from "./emailPreference";

/**
 * Get a user's profile by their auth user ID
 */
export async function getProfile(userId: string): Promise<UserProfile | null> {
  if (!isSupabaseConfigured() || !supabase) {
    console.warn("Supabase not configured - cannot get profile");
    return null;
  }

  const { data, error } = await supabase
    .from("user_profiles")
    .select("*")
    .eq("user_id", userId)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // No rows returned - profile doesn't exist
      return null;
    }
    console.error("Error fetching profile:", error);
    return null;
  }

  return data as UserProfile;
}

/**
 * Create a new user profile.
 * For Open Alpha, only email is required. Display name and preferences are optional.
 */
export async function createProfile(
  userId: string,
  data: CreateUserProfileData,
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  const now = new Date().toISOString();

  const { error } = await supabase.from("user_profiles").insert({
    user_id: userId,
    email: data.email,
    display_name: data.display_name || data.email.split("@")[0],
    // PM ruling 2026-09-13: consent is affirmative, so an unset preference is
    // "none", not "updates". `||` not `??`, deliberately: the live column is
    // UNCONSTRAINED (measured - the table CHECKs `role` and not this column),
    // so `??` would store a stray "" verbatim in the field that decides whether
    // we mail someone, whereas `||` coerces it to a valid value.
    // ⚠ This function has ZERO live callers - profile rows are created by the
    // handle_new_user DB trigger, which still COALESCEs to 'updates'. Changing
    // this line is correct and changes nothing that runs.
    email_preference: data.email_preference || NO_EMAIL_PREFERENCE,
    privacy_agreed_at: data.privacy_agreed_at || now,
    terms_agreed_at: data.terms_agreed_at || now,
  });

  if (error) {
    // Unique constraint violation — profile already exists (race condition)
    if (error.code === "23505") {
      return { success: true };
    }
    console.error("Error creating profile:", error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Update a user's profile
 */
export async function updateProfile(
  userId: string,
  updates: Partial<
    Omit<
      UserProfile,
      | "id"
      | "user_id"
      | "created_at"
      | "updated_at"
      | "donor_status"
      | "total_donated"
      | "tshirt_eligible"
      | "donor_tier"
      | "subscription_credit"
      | "role"
    >
  >,
): Promise<{ success: boolean; error?: string }> {
  if (!isSupabaseConfigured() || !supabase) {
    return { success: false, error: "Supabase not configured" };
  }

  const { error } = await supabase
    .from("user_profiles")
    .update(updates)
    .eq("user_id", userId);

  if (error) {
    console.error("Error updating profile:", error);
    return { success: false, error: error.message };
  }

  return { success: true };
}

/**
 * Check if a profile exists for a user
 */
export async function profileExists(userId: string): Promise<boolean> {
  const profile = await getProfile(userId);
  return profile !== null;
}
